import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Divider, Select, Switch, Tooltip } from "antd";
import {
  DollarOutlined,
  CloseOutlined,
  SaveOutlined,
  StarOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import "./addAndUpdateOoraPlan.css";

const { Option } = Select;

const OoraPlanEditModal = ({ visible, onClose, planData, onSave }) => {
  const [form] = Form.useForm();
  const [features, setFeatures] = useState([{ name: "", enabled: false }]);

  useEffect(() => {
    if (visible && planData) {
      // Parse features from [[name, bool], ...] format
      if (planData.features && Array.isArray(planData.features)) {
        setFeatures(
          planData.features.map(([name, enabled]) => ({ name, enabled }))
        );
      } else {
        setFeatures([{ name: "", enabled: false }]);
      }
      form.setFieldsValue({
        ...planData,
        features: undefined, // handled separately
      });
    } else if (visible) {
      form.resetFields();
      setFeatures([{ name: "", enabled: false }]);
    }
  }, [visible, planData, form]);

  const handleFinish = (values) => {
    // Convert features back to [[name, bool], ...] format
    const featuresPayload = features
      .filter((f) => f.name.trim() !== "")
      .map((f) => [f.name, f.enabled]);

    onSave({ ...values, features: featuresPayload });
  };

  const addFeature = () => {
    setFeatures([...features, { name: "", enabled: false }]);
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const updateFeature = (index, key, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [key]: value };
    setFeatures(updated);
  };

  const currencies = ["INR", "USD", "EUR", "GBP"];

  const modalTitle = (
    <div className="oora-plan-edit-modal-header-content">
      <DollarOutlined
        className="oora-plan-edit-modal-header-icon"
        style={{ color: "white" }}
      />
      <span style={{ color: "white", marginLeft: "10px" }}>
        {planData?._id ? "EDIT PLAN" : "ADD PLAN"}
      </span>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      closeIcon={
        <CloseOutlined
          style={{
            color: "white",
            fontSize: "20px",
            // margin: "27px 35px 0px 0px",
          }}
        />
      }
      footer={null}
      destroyOnClose
      width={700}
      className="oora-plan-edit-modal-override"
      styles={{
        header: {
          background: "#0D152B",
          borderRadius: "12px 12px 0 0",
          padding: "20px 24px",
          borderBottom: "none",
        },
        body: {
          padding: "24px",
          background: "#fafafa",
        },
      }}
    >
      <div className="oora-plan-edit-modal-form">
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          {/* Basic Information */}
          <div className="oora-plan-edit-section">
            <h3 className="oora-plan-edit-section-header">Basic Information</h3>
            <div className="oora-plan-edit-grid">
              <Form.Item
                label="Plan Name"
                name="plan_name"
                rules={[{ required: true, message: "Please input plan name" }]}
              >
                <Input
                  size="large"
                  placeholder="Enter plan name"
                  className="oora-plan-edit-input-override"
                />
              </Form.Item>

              <Form.Item
                label="Amount"
                name="amount"
                rules={[{ required: true, message: "Please input amount" }]}
              >
                <Input
                  type="number"
                  size="large"
                  placeholder="Enter amount"
                  className="oora-plan-edit-input-override"
                />
              </Form.Item>
            </div>

            <div className="oora-plan-edit-grid">
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: "Please select currency" }]}
              >
                <Select
                  size="large"
                  placeholder="Select currency"
                  className="oora-plan-edit-input-override"
                >
                  {currencies.map((currency) => (
                    <Option key={currency} value={currency}>
                      {currency}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Before Discount Amount (Optional)"
                name="before_discount_amount"
              >
                <Input
                  type="number"
                  size="large"
                  placeholder="Enter original price"
                  className="oora-plan-edit-input-override"
                />
              </Form.Item>
            </div>

            {/* NEW: No. of Reports */}
            <div className="oora-plan-edit-grid">
              <Form.Item
                label="No. of Reports"
                name="no_of_reports"
                initialValue={1}
                rules={[
                  { required: true, message: "Please input number of reports" },
                ]}
              >
                <Input
                  type="number"
                  size="large"
                  min={1}
                  placeholder="Enter number of reports"
                  className="oora-plan-edit-input-override"
                />
              </Form.Item>
            </div>
          </div>

          <Divider className="oora-plan-edit-divider" />

          {/* Plan Features */}
          <div className="oora-plan-edit-section">
            <h3 className="oora-plan-edit-section-header">Plan Features</h3>

            <Form.Item label="Description" name="description">
              <Input.TextArea
                rows={3}
                placeholder="Enter plan description"
                className="oora-plan-edit-input-override"
              />
            </Form.Item>

            {/* Dynamic Features List */}
            <div className="oora-plan-features-label">Features</div>
            <div className="oora-plan-features-list">
              {features.map((feature, index) => (
                <div key={index} className="oora-plan-feature-row">
                  <Tooltip title={feature.enabled ? "Click to mark as Not Available" : "Click to mark as Available"}>
                    <div
                      className={`oora-plan-feature-availability ${feature.enabled ? "available" : "not-available"}`}
                      onClick={() => updateFeature(index, "enabled", !feature.enabled)}
                    >
                      {feature.enabled ? (
                        <><CheckCircleFilled className="oora-feature-icon" /> Available</>
                      ) : (
                        <><CloseCircleFilled className="oora-feature-icon" /> Not Available</>
                      )}
                    </div>
                  </Tooltip>
                  <Input
                    value={feature.name}
                    onChange={(e) =>
                      updateFeature(index, "name", e.target.value)
                    }
                    placeholder="Enter feature name"
                    className="oora-plan-edit-input-override oora-plan-feature-input"
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeFeature(index)}
                    disabled={features.length === 1}
                    className="oora-plan-feature-delete-btn"
                  />
                </div>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addFeature}
                className="oora-plan-add-feature-btn"
                block
              >
                Add Feature
              </Button>
            </div>
          </div>

          <Divider className="oora-plan-edit-divider" />

          {/* Status & Display */}
          <div className="oora-plan-edit-section">
            <h3 className="oora-plan-edit-section-header">Status & Display</h3>
            <div className="oora-plan-edit-grid">
              <Form.Item label="Status" name="status" initialValue="active">
                <Select size="large" className="oora-plan-edit-input-override">
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Position" name="position">
                <Input
                  type="number"
                  size="large"
                  placeholder="Enter display position"
                  className="oora-plan-edit-input-override"
                />
              </Form.Item>
            </div>

            <Form.Item
              label="Most Popular"
              name="is_most_popular"
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<StarOutlined />}
                unCheckedChildren={<StarOutlined />}
              />
            </Form.Item>
          </div>

          {/* Action Buttons */}
          <Form.Item style={{ marginBottom: 0 }}>
            <div className="oora-plan-edit-actions">
              <Button
                size="large"
                onClick={onClose}
                className="oora-plan-edit-btn oora-plan-edit-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                htmlType="submit"
                size="large"
                icon={<SaveOutlined />}
                className="oora-plan-edit-btn oora-plan-edit-btn-primary oora-plan-edit-btn-override"
              >
                <span style={{ color: "white" }}>
                  <p className="update-btn">
                    {planData?._id ? "Update Plan" : "Create Plan"}
                  </p>
                </span>
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default OoraPlanEditModal;